import { cloneSystemData, validateSystemData } from '../../plugin-contracts/system_data_contract.mjs';
import { assignOrderDataSnapshot, assignSystemDataSnapshot, orderDataSnapshot, systemDataSnapshot } from '../services/system_history_service.mjs';

function freeze(value) {
  if (value && typeof value === 'object') { Object.values(value).forEach(freeze); Object.freeze(value); }
  return value;
}
/** One owner, synchronous preparation, detached validation before either write. */
export function createSystemOrderCommandService(contract, ports) {
  let busy = false;
  const owner = contract.systemId;
  function read() {
    const entry = orderDataSnapshot(owner, ports.getProject());
    return freeze(cloneSystemData(entry?.data ?? contract.orderData.defaults));
  }
  function transaction(meta, update, includeDocument = true) {
    if (busy) throw new Error('Ein Auftragsbefehl wird bereits vorbereitet.');
    ports.assertIdle();
    if (!ports.authorize()) throw new Error('Keine Berechtigung für diese Systemänderung.');
    if (typeof update !== 'function') throw new TypeError('Auftragsbefehl benötigt eine Mutation.');
    const project = ports.getProject(), sheetId = ports.getSheetId();
    if (!project) throw new Error('Kein aktives Projekt.');
    const beforeOrder = orderDataSnapshot(owner, project);
    const beforeDocument = includeDocument ? systemDataSnapshot(contract, project) : null;
    const orderData = cloneSystemData(beforeOrder?.data ?? contract.orderData.defaults);
    const document = includeDocument ? cloneSystemData(beforeDocument.present ? beforeDocument.data : contract.defaults) : null;
    busy = true;
    try {
      const result = update(includeDocument ? Object.freeze({ document, orderData }) : orderData);
      if (result && typeof result.then === 'function') {
        Promise.resolve(result).catch(() => undefined);
        throw new TypeError('Auftragsbefehle müssen synchron sein.');
      }
      if (result === false) return result;
      validateSystemData(contract.orderData, orderData, contract.orderData.version);
      if (includeDocument) validateSystemData(contract, document, contract.version);
      const orderChanged = JSON.stringify(orderData) !== JSON.stringify(beforeOrder?.data ?? contract.orderData.defaults);
      const documentChanged = includeDocument && JSON.stringify(document) !== JSON.stringify(beforeDocument.present ? beforeDocument.data : contract.defaults);
      if (!orderChanged && !documentChanged) return result;
      if (project !== ports.getProject() || sheetId !== ports.getSheetId()) throw new Error('Projektkontext während des Befehls gewechselt.');
      if (!ports.authorize()) throw new Error('Keine Berechtigung für diese Systemänderung.');
      const afterOrder = orderChanged ? { ...beforeOrder, version: contract.orderData.version, data: cloneSystemData(orderData) } : beforeOrder;
      const afterDocument = documentChanged ? { present: true, version: contract.version, data: cloneSystemData(document) } : null;
      // Prepare path ancestors too: incompatible foreign values cannot cause a partial commit.
      const prepared = { ...project };
      if (documentChanged) assignSystemDataSnapshot(contract, afterDocument, prepared);
      if (orderChanged) assignOrderDataSnapshot(owner, afterOrder, prepared);
      const token = ports.recordUndo(String(meta?.label || 'Auftragseinstellungen'), documentChanged ? 'systems' : 'orderData');
      if (documentChanged) {
        project[contract.path[0]] = prepared[contract.path[0]];
        project.systemDataVersions = prepared.systemDataVersions;
      }
      if (orderChanged) project.systemOrderData = prepared.systemOrderData;
      ports.completeUndo(token, documentChanged && orderChanged ? { before: beforeOrder, after: afterOrder } : null);
      ports.commit(meta || {});
      return result;
    } finally { busy = false; }
  }
  return Object.freeze({ read, transaction, execute: (meta, update) => transaction(meta, update, false), isBusy: () => busy });
}
